import React, { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Athlete } from './AthleteCard'

interface SendMessageModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  athlete: Athlete | null
  onSubmit: (data: DirectMessageData) => void
  isSending?: boolean
}

export interface DirectMessageData {
  message: string
}

interface MessageFormProps {
  open: boolean
  athlete: Athlete
  onSubmit: (data: DirectMessageData) => void
  isSending?: boolean
  onCancel: () => void
}

const inputClassName =
  'border-foreground/15 bg-card text-foreground placeholder:text-gray-500 focus-visible:ring-[#C0FD72]/40 focus-visible:ring-offset-0'

// Isolated form component so typing only re-renders the textarea/buttons,
// not the athlete preview (avatar) above it.
const MessageForm: React.FC<MessageFormProps> = React.memo(
  ({ open, athlete, onSubmit, isSending = false, onCancel }) => {
    const [message, setMessage] = useState('')

    useEffect(() => {
      if (open) {
        setMessage('')
      }
    }, [open, athlete])

    const handleSubmit = useCallback(() => {
      if (!message.trim()) return
      onSubmit({ message: message.trim() })
    }, [message, onSubmit])

    return (
      <>
        {/* Message */}
        <div className="space-y-2">
          <Label htmlFor="directMessage" className="text-gray-700">
            Your message
          </Label>
          <p className="text-xs text-gray-500">
            Introduce yourself or your brand, and describe what you're looking for. You can book
            from their Content Shop anytime.
          </p>
          <Textarea
            id="directMessage"
            placeholder="Hi, this is [Name] from [Brand]. We love your content and have a question before we book..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            className={inputClassName}
          />
        </div>

        <DialogFooter className="border-t border-foreground/10 pt-4">
          <Button variant="dark-outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSending || !message.trim()} variant="lime">
            {isSending ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogFooter>
      </>
    )
  },
)
MessageForm.displayName = 'MessageForm'

export const SendMessageModal: React.FC<SendMessageModalProps> = ({
  open,
  onOpenChange,
  athlete,
  onSubmit,
  isSending = false,
}) => {
  const handleCancel = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  if (!athlete) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-foreground/15 bg-card text-foreground">
        <DialogHeader className="border-b border-foreground/10 pb-4">
          <DialogTitle className="text-foreground">Contact {athlete.name}</DialogTitle>
          <DialogDescription className="text-gray-600">
            Send a direct message to this athlete. They'll be able to reply in their inbox.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Athlete Preview */}
          <div className="flex items-center gap-3 p-3 rounded-lg border border-foreground/10 bg-card">
            {athlete.avatar ? (
              <img
                src={athlete.avatar}
                alt={athlete.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <span className="text-lg font-semibold text-gray-700">
                  {athlete.name.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <div className="font-medium text-foreground">{athlete.name}</div>
              <div className="text-sm text-gray-600">
                {athlete.school || 'Independent'} • {athlete.sports[0] || 'Athlete'}
              </div>
            </div>
          </div>

          <MessageForm
            open={open}
            athlete={athlete}
            onSubmit={onSubmit}
            isSending={isSending}
            onCancel={handleCancel}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
